import { ForwardedRef, MutableRefObject, useEffect, useMemo, useState } from 'react';

import { HStack } from '@direct-frontend/components';
import { debounce, genericForwardRef } from '@direct-frontend/stdlib';
import type { BaseRowData, LegacyDataProvider } from '@adv-frontend/data-provider';

import type { GridTable } from '../../../types';
import type { GridRowSelectionManager } from '../../../lib/selectionManager/selectionManager';
import { HeaderGroups } from '../HeaderGroups/HeaderGroups';

import styles from '../Grid.module.css';

/**
 * Пропсы компонента `Header`.
 *
 * @typeParam RowData Данные грида.
 */
type HeaderProps<RowData extends BaseRowData> = {
    /**
     * Инстанс грида.
     */
    grid: GridTable<RowData>;
    /**
     * Ссылка на scrollable элемент грида.
     */
    gridRef: MutableRefObject<HTMLDivElement | null>;
    /**
     * Ссылка на глобальный ресайзер грида.
     */
    commonResizerRef: MutableRefObject<HTMLDivElement | null>;
    /**
     * Датапровайдер.
     */
    dataProvider: LegacyDataProvider<RowData>;
    /**
     * Менеджер выделения строк.
     */
    selectionManager: GridRowSelectionManager | undefined;
    /**
     * Скрывать ли нижний бордер у шапки.
     */
    hideHeaderBorderBottom: boolean;
};

const debounceWaitMs = 500;

/**
 * Компонент шапки грида.
 *
 * @typeParam RowData Данные грида.
 */
export const Header = genericForwardRef(function Header<RowData extends BaseRowData>(
    { commonResizerRef, grid, gridRef, dataProvider, selectionManager, hideHeaderBorderBottom }: HeaderProps<RowData>,
    ref: ForwardedRef<HTMLDivElement>
) {
    const [visibleResizers, setVisibleResizers] = useState<Record<string, boolean>>();

    const gridTotalSize = grid.getTotalSize();

    const gridWidth = gridRef.current?.clientWidth;
    const gridRightTotalSize = grid.getRightTotalSize();

    const pinnedRightGroupOffset = useMemo(() => {
        if (gridWidth) {
            return gridWidth - gridRightTotalSize;
        }

        return;
    }, [gridRightTotalSize, gridWidth]);

    /**
     * Составляем объект с признаками видимости для ресайзеров в каждой ячейке центральной секции шапки грида.
     * Это необходимо, чтобы скрывать ресайзеры в ячейках,
     * которые находятся под запиненной областью (запиненными колонками) и отображать в видимой области секции.
     */
    const updateVisibleResizers = useMemo(
        () =>
            debounce(() => {
                const gridWidth = gridRef.current?.clientWidth;
                const gridScrollLeft = gridRef.current?.scrollLeft;
                const gridRightTotalSize = grid.getRightTotalSize();
                const gridLeftTotalSize = grid.getLeftTotalSize();

                // Берем заголовки и 1 и 2 уровня, для того, чтобы скрывать ресайзеры и группы и отдельных колонок
                const centerHeaders = [
                    ...(grid.getCenterHeaderGroups()[0]?.headers ?? []),
                    ...(grid.getCenterHeaderGroups()[1]?.headers ?? []),
                ];

                /**
                 * Ресайзер отображается, если он находится в видимой области центральной секции, то есть:
                 * - справа от левой запинненой области (первое условие) и
                 * - слева от правой запиненной области (второе условие).
                 */
                if (gridScrollLeft !== undefined && gridWidth) {
                    const newVisibleResizers = centerHeaders?.reduce<Record<string, boolean>>(
                        (acc, header) => ({
                            ...acc,
                            [header.id]:
                                // Округляем размеры колонок, так как они могут быть дробными
                                // А значения ширины блоков и скролла всегда целые числа
                                Math.floor(header.getStart() + header.getSize()) > gridScrollLeft &&
                                Math.floor(header.getStart() + header.getSize()) - gridScrollLeft <=
                                    gridWidth - gridLeftTotalSize - gridRightTotalSize,
                        }),
                        {}
                    );

                    setVisibleResizers(newVisibleResizers);
                }
            }, debounceWaitMs),
        [grid, gridRef]
    );

    useEffect(() => {
        const grid = gridRef.current;
        grid?.addEventListener('scroll', updateVisibleResizers);

        return () => grid?.removeEventListener('scroll', updateVisibleResizers);
    }, [gridRef, updateVisibleResizers]);

    useEffect(() => updateVisibleResizers(), [gridTotalSize, updateVisibleResizers]);

    const headerStyles = useMemo(() => ({ minWidth: `${gridTotalSize}px` }), [gridTotalSize]);

    return (
        <div style={headerStyles} className={styles.header}>
            <HStack ref={ref}>
                <HStack className={styles.headerContent}>
                    {grid.getIsSomeColumnsPinned('left') && (
                        <HeaderGroups
                            className={styles.pinnedLeftHeaders}
                            commonResizerRef={commonResizerRef}
                            grid={grid}
                            gridRef={gridRef}
                            headerGroups={grid.getLeftHeaderGroups()}
                            subHeaders={grid.getLeftLeafHeaders()}
                            dataProvider={dataProvider}
                            selectionManager={selectionManager}
                            hideHeaderBorderBottom={hideHeaderBorderBottom}
                        />
                    )}
                    <HeaderGroups
                        className={styles.centerHeaders}
                        commonResizerRef={commonResizerRef}
                        grid={grid}
                        gridRef={gridRef}
                        groupLeftOffset={grid.getLeftTotalSize()}
                        headerGroups={grid.getCenterHeaderGroups()}
                        subHeaders={grid.getCenterLeafHeaders()}
                        visibleResizers={visibleResizers}
                        dataProvider={dataProvider}
                        selectionManager={selectionManager}
                        hideHeaderBorderBottom={hideHeaderBorderBottom}
                    />
                    {grid.getIsSomeColumnsPinned('right') && (
                        <HeaderGroups
                            className={styles.pinnedRightHeaders}
                            commonResizerRef={commonResizerRef}
                            grid={grid}
                            gridRef={gridRef}
                            groupLeftOffset={pinnedRightGroupOffset}
                            headerGroups={grid.getRightHeaderGroups()}
                            subHeaders={grid.getRightLeafHeaders()}
                            dataProvider={dataProvider}
                            selectionManager={selectionManager}
                            hideHeaderBorderBottom={hideHeaderBorderBottom}
                        />
                    )}
                </HStack>
            </HStack>
        </div>
    );
});
